import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '@/shared/components/ui/button'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
          <div className="text-center max-w-md px-6">
            <h1 className="mb-3 text-[32px] text-[var(--ink)] font-display">
              Something went wrong
            </h1>
            <p className="mb-8 text-[13px] text-[var(--ink-3)]">
              An unexpected error occurred. Please refresh the page or return to
              the dashboard.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
              >
                Refresh page
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  this.setState({ hasError: false })
                  window.location.href = '/dashboard'
                }}
                className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
              >
                Go to dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children
  }
}
